import React from "react";
import {
  Button,
  Col,
  Row,
  Typography,
  Select,
  InputNumber,
  Card,
  Space,
  Tag,
  Divider,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  InputText,
  SimpleInputSelectMultiple,
} from "../../../../shared/components";
import {
  useCreatePromotion,
  SelectedDaysType,
  CategoryConfig,
  ExcludedProduct,
  FreeSlotProduct,
} from "../../hooks/useCreatePromotion";
import { useCategories, ICategory } from "../../hooks/useCategories";
import { IPromotionRule } from "../../hooks/usePromotions";
import { PromotionExcludedProducts } from "./PromotionExcludedProducts";
import { PromotionFreeSlotProducts } from "./PromotionFreeSlotProducts";

const { Text, Title } = Typography;
const { Option } = Select;

interface PromotionFormProps {
  setOpenModal: (val: boolean) => void;
  onSuccess?: () => void;
  promotion?: IPromotionRule | null;
}

export const PromotionForm: React.FC<PromotionFormProps> = ({
  setOpenModal,
  onSuccess,
  promotion,
}) => {
  const isEditing = Boolean(promotion);

  const initialDays = React.useMemo<SelectedDaysType>(
    () => ({
      monday: (promotion?.monday as "true" | "false") || "false",
      tuesday: (promotion?.tuesday as "true" | "false") || "false",
      wednesday: (promotion?.wednesday as "true" | "false") || "false",
      thursday: (promotion?.thursday as "true" | "false") || "false",
      friday: (promotion?.friday as "true" | "false") || "false",
      saturday: (promotion?.saturday as "true" | "false") || "false",
      sunday: (promotion?.sunday as "true" | "false") || "false",
    }),
    [promotion],
  );

  const initialSelectedDays = React.useMemo<string[]>(() => {
    return Object.entries(initialDays)
      .filter(([, v]) => v === "true")
      .map(([k]) => k);
  }, [initialDays]);

  const initialCategories = React.useMemo<CategoryConfig[]>(() => {
    if (promotion?.rule_categories?.length) {
      return promotion.rule_categories.map((c) => ({
        category_id: c.category_id,
        role: c.role,
        required_quantity: c.required_quantity,
        is_required: c.is_required === "true",
      }));
    }
    return [
      {
        category_id: 0,
        role: "primary",
        required_quantity: 2,
        is_required: true,
      },
    ];
  }, [promotion]);

  const initialExcludedProducts = React.useMemo<ExcludedProduct[]>(() => {
    if (promotion?.excluded_products?.length) {
      return promotion.excluded_products.map((p) => ({
        id: p.product_id,
        name: p.product?.name || `Producto #${p.product_id}`,
      }));
    }
    return [];
  }, [promotion]);

  const initialFreeSlotProducts = React.useMemo<FreeSlotProduct[]>(() => {
    if (promotion?.free_products?.length) {
      return promotion.free_products.map((p) => ({
        id: p.product_id,
        name: p.product?.name || `Producto #${p.product_id}`,
      }));
    }
    return [];
  }, [promotion]);

  const [days, setDays] = React.useState<SelectedDaysType>(initialDays);
  const [selectedDays, setSelectedDays] = React.useState<string[]>(
    initialSelectedDays,
  );
  const [promoType, setPromoType] = React.useState<"2x1" | "3x2" | "custom">(
    (promotion?.promo_type as "2x1" | "3x2" | "custom") || "2x1",
  );
  const [categories, setCategories] =
    React.useState<CategoryConfig[]>(initialCategories);
  const [excludedProducts, setExcludedProducts] = React.useState<
    ExcludedProduct[]
  >(initialExcludedProducts);
  const [freeSlotProducts, setFreeSlotProducts] = React.useState<
    FreeSlotProduct[]
  >(initialFreeSlotProducts);

  React.useEffect(() => {
    setDays(initialDays);
    setSelectedDays(initialSelectedDays);
    setPromoType((promotion?.promo_type as "2x1" | "3x2" | "custom") || "2x1");
    setCategories(initialCategories);
    setExcludedProducts(initialExcludedProducts);
    setFreeSlotProducts(initialFreeSlotProducts);
  }, [
    promotion?.id,
    initialDays,
    initialSelectedDays,
    initialCategories,
    initialExcludedProducts,
    initialFreeSlotProducts,
    promotion?.promo_type,
  ]);

  const { categories: availableCategories, loading: loadingCategories } = useCategories();
  
  const { control, handleSubmit, errors, isSubmitting } = useCreatePromotion({
    setOpenModal,
    days,
    categories,
    excludedProducts,
    freeSlotProducts,
    promoType,
    onSuccess,
    editingId: promotion?.id ?? null,
    defaultValues: promotion
      ? {
          name: promotion.name,
          description: promotion.description ?? undefined,
        }
      : undefined,
  });

  const handleDaysChange = (values: string[]) => {
    setSelectedDays(values);
    const updatedDays: SelectedDaysType = {
      monday: values.includes("monday") ? "true" : "false",
      tuesday: values.includes("tuesday") ? "true" : "false",
      wednesday: values.includes("wednesday") ? "true" : "false",
      thursday: values.includes("thursday") ? "true" : "false",
      friday: values.includes("friday") ? "true" : "false",
      saturday: values.includes("saturday") ? "true" : "false",
      sunday: values.includes("sunday") ? "true" : "false",
    };
    setDays(updatedDays);
  };

  const handlePromoTypeChange = (value: "2x1" | "3x2" | "custom") => {
    setPromoType(value);
    // Auto-configurar cantidades según el tipo
    if (value === "2x1") {
      // 2x1: 2 productos de categoría principal
      setCategories(categories.map((c, i) => ({
        ...c,
        required_quantity: c.role === "primary" ? 2 : 1,
      })));
    } else if (value === "3x2") {
      // 3x2: 2 de categoría principal + 1 de otra categoría (el más barato gratis)
      setCategories(categories.map((c) => ({
        ...c,
        required_quantity: c.role === "primary" ? 2 : 1,
      })));
    }
  };

  const addCategory = (role: "primary" | "secondary") => {
    const defaultQty = role === "primary" ? (promoType === "2x1" ? 2 : promoType === "3x2" ? 2 : 1) : 1;
    setCategories([
      ...categories,
      {
        category_id: 0,
        role,
        required_quantity: defaultQty,
        is_required: role === "primary",
      },
    ]);
  };

  const removeCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const updateCategory = (index: number, field: keyof CategoryConfig, value: any) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const getCategoryName = (id: number) => {
    const cat = availableCategories.find((c) => c.id === id);
    return cat?.name || "";
  };

  const primaryCategories = categories.filter((c) => c.role === "primary");
  const secondaryCategories = categories.filter((c) => c.role === "secondary");

  return (
    <form onSubmit={handleSubmit}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text strong>Nombre de la promoción:</Text>
          <InputText placeHolder="Ej: Martes de 2x1" name="name" control={control} />
        </Col>

        <Col span={24}>
          <Text strong>Descripción (opcional):</Text>
          <InputText placeHolder="Descripción de la promo" name="description" control={control} />
        </Col>

        <Col span={24}>
          <Text strong>Tipo de promoción:</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            value={promoType}
            onChange={handlePromoTypeChange}
          >
            <Option value="2x1">2x1 - Lleva 2, paga 1 (el más barato gratis)</Option>
            <Option value="3x2">3x2 - Lleva 3, paga 2 (el más barato gratis)</Option>
            <Option value="custom">Personalizada</Option>
          </Select>
        </Col>

        <Col span={24}>
          <Text strong>Días disponibles:</Text>
          <div style={{ marginTop: 8 }}>
            <SimpleInputSelectMultiple
              label="Selecciona los días"
              valuesSelect={selectedDays}
              onChange={handleDaysChange}
              clearValue={(value: string) =>
                handleDaysChange(selectedDays.filter((day) => day !== value))
              }
              showSearch={false}
              dataOptions={[
                { title: "Lunes", value: "monday" },
                { title: "Martes", value: "tuesday" },
                { title: "Miércoles", value: "wednesday" },
                { title: "Jueves", value: "thursday" },
                { title: "Viernes", value: "friday" },
                { title: "Sábado", value: "saturday" },
                { title: "Domingo", value: "sunday" },
              ]}
            />
          </div>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: '12px 0' }} />
          <Title level={4} style={{ marginBottom: 24 }}>Configuración de Categorías</Title>
        </Col>

        <Col span={24}>
          <Text strong>Categoría Principal</Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            El cliente debe escoger productos de esta categoría
          </Text>
          
          {primaryCategories.map((cat, idx) => {
            const realIndex = categories.findIndex((c) => c === cat);
            return (
              <Card key={idx} size="small" style={{ marginBottom: 12, background: '#fafafa', border: '1px solid #e8e8e8' }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Categoría</Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Selecciona categoría"
                      value={cat.category_id || undefined}
                      onChange={(val) => updateCategory(realIndex, "category_id", val)}
                      loading={loadingCategories}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {availableCategories.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={18} sm={8}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Cantidad requerida</Text>
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      max={10}
                      value={cat.required_quantity}
                      onChange={(val) => updateCategory(realIndex, "required_quantity", val || 1)}
                    />
                  </Col>
                  <Col xs={6} sm={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '100%' }}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeCategory(realIndex)}
                      style={{ marginTop: 24 }}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}
          
          {primaryCategories.length === 0 && (
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() => addCategory("primary")}
              style={{ marginBottom: 16, height: 48 }}
            >
              Agregar Categoría Principal
            </Button>
          )}
        </Col>

        {promoType !== "custom" && (
          <Col span={24}>
            <Divider style={{ margin: "12px 0" }} />
            <Title level={5} style={{ marginBottom: 8 }}>
              Productos para el ítem gratis
            </Title>
            <PromotionFreeSlotProducts
              freeSlotProducts={freeSlotProducts}
              onChange={setFreeSlotProducts}
              categories={categories}
              excludedProducts={excludedProducts}
              promoType={promoType}
            />
          </Col>
        )}

        {/* Categorías Secundarias */}
        <Col span={24}>
          <Divider style={{ margin: '12px 0' }} />
          <Text strong>Categorías Secundarias (Opcionales)</Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            El cliente puede escoger de estas categorías adicionales
          </Text>

          {secondaryCategories.map((cat, idx) => {
            const realIndex = categories.findIndex((c) => c === cat);
            return (
              <Card key={idx} size="small" style={{ marginBottom: 12, background: '#fafafa', border: '1px solid #e8e8e8' }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Categoría</Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Selecciona categoría"
                      value={cat.category_id || undefined}
                      onChange={(val) => updateCategory(realIndex, "category_id", val)}
                      loading={loadingCategories}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {availableCategories.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={18} sm={8}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Cantidad requerida</Text>
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      max={10}
                      value={cat.required_quantity}
                      onChange={(val) => updateCategory(realIndex, "required_quantity", val || 1)}
                    />
                  </Col>
                  <Col xs={6} sm={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '100%' }}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeCategory(realIndex)}
                      style={{ marginTop: 24 }}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}

          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => addCategory("secondary")}
            style={{ marginBottom: 16, height: 48 }}
          >
            Agregar Categoría Secundaria
          </Button>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "12px 0" }} />
          <Title level={4} style={{ marginBottom: 16 }}>
            Productos excluidos (opcional)
          </Title>
          <PromotionExcludedProducts
            excludedProducts={excludedProducts}
            onChange={setExcludedProducts}
            categories={categories}
          />
        </Col>

        {/* Resumen */}
        {categories.length > 0 && (
          <Col span={24}>
            <Card 
              size="small" 
              style={{ 
                background: "#f0f5ff", 
                borderColor: "#adc6ff",
                marginTop: 16 
              }}
            >
              <Text strong style={{ color: '#1d39c4', fontSize: 16 }}>Resumen de la promoción:</Text>
              <br />
              <div style={{ marginTop: 12 }}>
                <Text>
                  {promoType === "2x1" && "El cliente lleva 2 productos, paga 1 (el más barato es gratis)"}
                  {promoType === "3x2" && "El cliente lleva 3 productos, paga 2 (el más barato es gratis)"}
                  {promoType === "custom" && "Promoción personalizada"}
                </Text>
                <br />
                {primaryCategories.length > 0 && (
                  <Text style={{ display: 'block', marginTop: 8 }}>
                    • Debe escoger <strong>{primaryCategories[0].required_quantity}</strong> producto(s) de{" "}
                    <strong>{getCategoryName(primaryCategories[0].category_id) || "una categoría"}</strong>
                  </Text>
                )}
                {secondaryCategories.length > 0 && (
                  <Text style={{ display: 'block', marginTop: 4 }}>
                    • Puede escoger de:{" "}
                    <strong>
                      {secondaryCategories
                        .map((c) => `${c.required_quantity} de ${getCategoryName(c.category_id) || "una categoría"}`)
                        .join(" ó ")}
                    </strong>
                  </Text>
                )}
                {freeSlotProducts.length > 0 && (
                  <Text style={{ display: "block", marginTop: 8, color: "#389e0d" }}>
                    • Elegibles como ítem gratis:{" "}
                    <strong>{freeSlotProducts.map((p) => p.name).join(", ")}</strong>
                  </Text>
                )}
                {excludedProducts.length > 0 && (
                  <Text style={{ display: "block", marginTop: 8, color: "#d46b08" }}>
                    • Excluidos de la promo (tarifa plena):{" "}
                    <strong>{excludedProducts.map((p) => p.name).join(", ")}</strong>
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        )}

        <Col span={24} style={{ marginTop: 24, marginBottom: 24 }}>
          <Button
            htmlType="submit"
            type="primary"
            shape="round"
            size="large"
            block
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              categories.filter((c) => c.category_id > 0).length === 0 ||
              selectedDays.length === 0
            }
          >
            {isEditing ? "Guardar Cambios" : "Crear Promoción"}
          </Button>
          {errors.name?.message && (
            <Text type="danger" style={{ display: "block", marginTop: 8, textAlign: "center" }}>
              {errors.name.message}
            </Text>
          )}
        </Col>
      </Row>
    </form>
  );
};
